import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ArrowRight,
  Check,
  Search,
  Sparkles,
  X,
} from 'lucide-react';
import { PROMPT_CATEGORIES, PROMPT_SUGGESTIONS } from './promptSuggestionsData';

const PromptSuggestionsSidebar = ({
  onSelectPrompt,
  onClose,
  currentPrompt,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [appliedId, setAppliedId] = useState(null);

  const filteredPrompts = useMemo(() => {
    return PROMPT_SUGGESTIONS.filter((item) => {
      const matchesCategory =
        selectedCategory === 'All' ||
        item.category.toLowerCase() === selectedCategory.toLowerCase() ||
        (selectedCategory === 'Users' &&
          (item.category === 'Users' ||
            item.category === 'Buyers' ||
            item.category === 'Sellers' ||
            item.category === 'Conversion'));

      const matchesSearch =
        searchTerm.trim() === '' ||
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.prompt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesCategory && matchesSearch;
    });
  }, [searchTerm, selectedCategory]);

  const handleCardClick = (item) => {
    // Only pass the prompt text, not the title, as explicitly requested
    onSelectPrompt(item.prompt);
    setAppliedId(item.id);
  };

  return (
    <aside className="ai-suggestions-panel" aria-label="Prompt Suggestions">
      {/* Header */}
      <div className="ai-suggestions-header">
        <div className="ai-suggestions-header-title">
          <div className="ai-suggestions-icon-badge">
            <Sparkles size={16} />
          </div>
          <div>
            <h5 className="mb-0">Suggested Prompts</h5>
            <span className="ai-suggestions-count">
              {PROMPT_SUGGESTIONS.length} ready-to-use prompts
            </span>
          </div>
        </div>

        <button
          type="button"
          className="ai-suggestions-close-btn"
          onClick={onClose}
          title="Close suggestions panel"
        >
          <X size={18} />
        </button>
      </div>

      {/* Search Input */}
      <div className="ai-suggestions-search-wrap">
        <Search size={15} className="ai-suggestions-search-icon" />
        <input
          type="text"
          className="ai-suggestions-search-input"
          placeholder="Search prompts (e.g. sales, user, bidding)..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            type="button"
            className="ai-suggestions-search-clear"
            onClick={() => setSearchTerm('')}
            title="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Category Pills */}
      <div className="ai-suggestions-categories">
        {PROMPT_CATEGORIES.map((cat) => (
          <button
            key={cat}
            type="button"
            className={`ai-cat-pill ${
              selectedCategory === cat ? 'active' : ''
            }`}
            onClick={() => setSelectedCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Prompts List */}
      <div className="ai-suggestions-list">
        {filteredPrompts.length === 0 ? (
          <div className="ai-suggestions-empty">
            <p className="mb-1">No matching prompts found.</p>
            <span className="text-muted">
              Try a different keyword or category.
            </span>
          </div>
        ) : (
          filteredPrompts.map((item) => {
            const isApplied =
              appliedId === item.id || currentPrompt === item.prompt;

            return (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                className={`ai-suggestion-card ${isApplied ? 'applied' : ''}`}
                onClick={() => handleCardClick(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleCardClick(item);
                  }
                }}
              >
                <div className="ai-suggestion-card-top">
                  <div className="ai-suggestion-badge-row">
                    <span className="ai-suggestion-num">
                      #{String(item.id).padStart(2, '0')}
                    </span>
                    <span className="ai-suggestion-category">
                      {item.category}
                    </span>
                  </div>
                  {isApplied && (
                    <span className="ai-suggestion-applied-badge">
                      <Check size={12} /> Selected
                    </span>
                  )}
                </div>

                <h6 className="ai-suggestion-title">{item.title}</h6>
                <p className="ai-suggestion-prompt-text">{item.prompt}</p>

                <div className="ai-suggestion-card-action">
                  <span>Autofill prompt</span>
                  <ArrowRight size={14} className="ai-arrow-icon" />
                </div>
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
};

PromptSuggestionsSidebar.propTypes = {
  onSelectPrompt: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  currentPrompt: PropTypes.string,
};

PromptSuggestionsSidebar.defaultProps = {
  currentPrompt: '',
};

export default PromptSuggestionsSidebar;
